import request from 'superagent';
import { v1 as uuidv1 } from 'uuid';

import authConfigInstance from './authServise';
import { getUserToken } from './tokenManager';

type Method = 'get' | 'post' | 'put' | 'patch' | 'delete';

class Client {
  baseUrl: string;
  accept: string;
  agent: request.SuperAgentStatic & request.Request;

  constructor(baseUrl: string, accept = 'application/json') {
    this.baseUrl = baseUrl;
    this.accept = accept;
    this.agent = request.agent();
    this.agent.accept(this.accept);
  }

  async request(
    method: Method,
    endpoint: string,
    data: Record<string, unknown> | Record<symbol, unknown> | null = null,
  ) {
    const url = /^http?:\/\//.test(endpoint) ? endpoint : `${this.baseUrl}${endpoint}`;
    let promise = this.agent[method](url);

    const token = await getUserToken();
    if (token) promise = promise.set('Authorization', `Bearer ${token}`);

    promise = promise.set('Cache-Control', 'no-cache');
    promise = promise.set('Cache-Control', 'max-age=0');
    promise = promise.set('x-request-id', uuidv1());

    if (['post', 'put', 'patch'].includes(method) && data) {
      promise = promise.send(data);
    }

    if (this.accept === 'text/html') {
      const response = await promise;

      return response.text;
    }
    try {
      const { body } = await promise;

      return body;
    } catch (error: any) {
      if (error?.status === 401) {
        console.warn('401 Unauthorized — redirecting to login');
        await authConfigInstance.signIn();
      }
      throw error;
    }
  }

  async postFormWithFiles(
    endpoint: string,
    formData?: Record<string, string>,
    files?: Record<string, File[]>,
  ) {
    const url = /^http?:\/\//.test(endpoint) ? endpoint : `${this.baseUrl}${endpoint}`;
    let promise = this.agent.post(url);

    const token = await getUserToken();
    if (token) promise = promise.set('Authorization', `Bearer ${token}`);

    promise = promise.set('Cache-Control', 'no-cache');
    promise = promise.set('Cache-Control', 'max-age=0');
    promise = promise.set('x-request-id', uuidv1());
    if (formData) {
      for (const key in formData) {
        promise.field(key, formData[key]);
      }
    }

    if (files) {
      for (const key in files) {
        files[key].forEach((file: any) => promise.attach(key, file));
      }
    }

    const { body } = await promise;

    return body;
  }

  async getBlob(endpoint: string): Promise<Blob> {
    const url = /^http?:\/\//.test(endpoint) ? endpoint : `${this.baseUrl}${endpoint}`;
    let promise = this.agent.get(url).responseType('blob');

    const token = await getUserToken();
    if (token) promise = promise.set('Authorization', `Bearer ${token}`);

    promise = promise.set('Cache-Control', 'no-cache');
    promise = promise.set('Cache-Control', 'max-age=0');
    promise = promise.set('x-request-id', uuidv1());
    const { body } = await promise;

    return body;
  }

  get(endpoint: string) {
    return this.request('get', endpoint);
  }

  post(endpoint: string, data: Record<string, unknown>) {
    return this.request('post', endpoint, data);
  }

  put(endpoint: string, data: Record<string, unknown>) {
    return this.request('put', endpoint, data);
  }

  patch(endpoint: string, data: Record<string, unknown>) {
    return this.request('patch', endpoint, data);
  }

  delete(endpoint: string) {
    return this.request('delete', endpoint);
  }
}

export default Client;
